import { Navigate, useLocation } from "react-router-dom";
import { usePermission } from "@src/presentation/hooks/usePermission";
import { useAuth } from "@src/presentation/contexts/AuthContext";
import LoadingScreen from "@src/presentation/components/Loading";
import type { Permission } from "@src/shared/constants/permissions";
import type { ReactNode } from "react";

interface RouteGuardProps {
    children: ReactNode;
    /** Single permission required to access route */
    permission?: Permission | string;
    /** Array of permissions - user must have ALL to access route */
    requiredPermissions?: (Permission | string)[];
    /** Array of permissions - user must have ANY ONE to access route */
    anyPermissions?: (Permission | string)[];
}

/**
 * RouteGuard
 * Wraps individual route components to check permissions
 * Redirects to /unauthorized if user doesn't have required permissions
 */
export const RouteGuard = ({
    children,
    permission,
    requiredPermissions,
    anyPermissions,
}: RouteGuardProps) => {
    const { hasPermission, hasAllPermissions, hasAnyPermission, isSuperAdmin } = usePermission();
    const { loading, user } = useAuth();
    const location = useLocation();

    if (loading || !user) {
        return <LoadingScreen />;
    }

    // If no permissions are specified, allow access
    if (!permission && !requiredPermissions && !anyPermissions) {
        return <>{children}</>;
    }

    // Super admins always have access
    if (isSuperAdmin()) {
        return <>{children}</>;
    }

    // Check permissions
    let hasAccess = false;

    if (permission) {
        hasAccess = hasPermission(permission);
    } else if (requiredPermissions && requiredPermissions.length > 0) {
        hasAccess = hasAllPermissions(requiredPermissions);
    } else if (anyPermissions && anyPermissions.length > 0) {
        hasAccess = hasAnyPermission(anyPermissions);
    }

    if (!hasAccess) {
        return <Navigate to="/unauthorized" replace state={{ from: location }} />;
    }

    return <>{children}</>;
};
